import { ApiResponse } from "api/api.d";
import { api } from "api/api";
import {
  AllSavingsReq,
  AllSavingsResponse,
  CalSavingResponse,
  CalSavingsIRateReq,
  ConfirmActionSavingsAccountReq,
  ConfirmActionSavingsAccountResponse,
  ConfirmCreateSavingReq,
  CreateSavingReq,
  CreateSavingResponse,
  InitActionSavingsAccountReq,
  PreviewSavingsResponse,
  SavingInterestTypeReq,
  SavingInterestTypeResponse,
  SavingListResponse,
  SavingsDetailResponse, 
  SavingsRolloverHistoryReq,
  SavingsRolloverHistoryResponse,
  SavingsTransactionHistoryReq,
  SavingsTransHistoryResponse,
} from "@/dto/savings/Savings";

const savingsUrl = "/savings-service/rest/web/request";
export const savingsAPI = api.injectEndpoints({
  endpoints: (builder) => ({
    getSavingsProducts: builder.query<ApiResponse<SavingListResponse>, void>({
      query: () => ({
        url: savingsUrl,
        method: "POST",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        functionName: "getSavingsProducts",
      }),
    }),
    createSavingAccount: builder.mutation<
      ApiResponse<CreateSavingResponse>,
      CreateSavingReq
    >({
      query: (data) => ({
        url: savingsUrl,
        method: "POST",
        functionName: "initSavingsOpen",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        data,
      }),
    }),
    confirmCreateSavingAccount: builder.mutation<
      ApiResponse<any>,
      ConfirmCreateSavingReq
    >({
      query: (data) => ({
        url: savingsUrl,
        method: "POST",
        functionName: "confirmSavingsOpen",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        data,
      }),
    }),
    calSavingIRate: builder.query<
      ApiResponse<CalSavingResponse>,
      CalSavingsIRateReq
    >({
      query: (data) => ({
        url: savingsUrl,
        method: "POST",
        functionName: "calculateSavingsInterest",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        data,
      }),
    }),
    getSavingsInterestByType: builder.query<
      ApiResponse<SavingInterestTypeResponse>,
      SavingInterestTypeReq
    >({
      query: (data) => ({
        url: savingsUrl,
        method: "POST",
        functionName: "getSavingsTerms",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        data,
      }),
    }),
    getAllSavings: builder.query<
      ApiResponse<AllSavingsResponse>,
      AllSavingsReq
    >({
      query: (data) => ({
        url: savingsUrl,
        method: "POST",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        functionName: "getSavingsList",
        data,
      }),
      providesTags: ["allSavings"],
    }),
    updateSavingAccountAlias: builder.mutation<
      ApiResponse<any>,
      { accountNo: string; alias: string }
    >({
      query: ({ accountNo, alias }) => ({
        url: savingsUrl,
        method: "POST",
        functionName: "updateSavingsAlias",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        data: {
          accountNo,
          alias,
        },
      }),
      invalidatesTags: ["allSavings"],
    }),

    updateFavoriteSavingsAccount: builder.mutation<
      ApiResponse<any>,
      { accountNo: string }
    >({
      query: ({ accountNo }) => ({
        url: savingsUrl,
        method: "POST",
        functionName: "updateSavingsFavorite",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        data: {
          accountNo,
        },
      }),
      invalidatesTags: ["allSavings"],
    }),

    getDetailSavingsAccount: builder.query<
      ApiResponse<SavingsDetailResponse>,
      { accountNo: string }
    >({
      query: ({ accountNo }) => ({
        url: savingsUrl,
        method: "POST",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        functionName: "getSavingsDetail",
        data: {
          accountNo,
        },
      }),
    }),
    getSavingsAccountTransHistories: builder.query<
      ApiResponse<SavingsTransHistoryResponse>,
      SavingsTransactionHistoryReq
    >({
      query: (data) => ({
        url: savingsUrl,
        method: "POST",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        functionName: "getSavingsTransactions",
        data,
      }),
    }),
    getSavingsRolloverHistories: builder.query<
      ApiResponse<SavingsRolloverHistoryResponse>,
      SavingsRolloverHistoryReq
    >({
      query: (data) => ({
        url: "/savings-service/rest/web/request",
        method: "POST",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        functionName: "getSavingsRollovers",
        data,
      }),
    }),
    exportSavingsAccountTransHistories: builder.mutation<
      ApiResponse<any>,
      SavingsTransactionHistoryReq
    >({
      query: (data) => ({
        url: "/savings-service/rest/web/request",
        method: "POST",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        functionName: "exportSavingsTransactions",
        data,
      }),
    }),
    exportSavingsRolloverHistories: builder.mutation<
      ApiResponse<any>,
      SavingsRolloverHistoryReq
    >({
      query: (data) => ({
        url: savingsUrl,
        method: "POST",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        functionName: "exportSavingsRollovers",
        data,
      }),
    }),
    downloadSavingsContract: builder.mutation<
      ApiResponse<any>,
      { accountNo: string }
    >({
      query: (data) => ({
        url: savingsUrl,
        method: "POST",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        functionName: "downloadSavingsContract",
        data,
      }),
    }),
    previewActionSavingsAccount: builder.query<
      ApiResponse<PreviewSavingsResponse>,
      { accountNo: string; action: string }
    >({
      query: (data) => ({
        url: savingsUrl,
        method: "POST",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        functionName: "previewSavingsClose",
        data,
      }),
    }),
    initActionSavingsAccount: builder.mutation<
      ApiResponse<any>,
      InitActionSavingsAccountReq
    >({
      query: (data) => ({
        url: savingsUrl,
        method: "POST",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        functionName: "initSavingsClose",
        data,
      }),
    }),
    confirmActionSavingsAccount: builder.mutation<
      ApiResponse<ConfirmActionSavingsAccountResponse>,
      ConfirmActionSavingsAccountReq
    >({
      query: (data) => ({
        url: savingsUrl,
        method: "POST",
        categoryCode: "CORP_D_SAVINGS_REGULAR",
        functionName: "confirmSavingsClose",
        data: {
          refId: data.refId,
          authObject: {
            authType: data.authType,
            otpId: data.otpId,
            otp: data.otp,
          },
        },
      }),
    }),
  }),
});

export const {
  useGetSavingsProductsQuery,
  useCreateSavingAccountMutation,
  useLazyCalSavingIRateQuery,
  useGetSavingsInterestByTypeQuery,
  useConfirmCreateSavingAccountMutation,
  useGetAllSavingsQuery,
  useUpdateSavingAccountAliasMutation,
  useUpdateFavoriteSavingsAccountMutation,
  useGetDetailSavingsAccountQuery,
  useLazyGetSavingsAccountTransHistoriesQuery,
  useLazyGetSavingsRolloverHistoriesQuery,
  useExportSavingsAccountTransHistoriesMutation,
  useExportSavingsRolloverHistoriesMutation,
  useDownloadSavingsContractMutation,
  usePreviewActionSavingsAccountQuery,
  useInitActionSavingsAccountMutation,
  useConfirmActionSavingsAccountMutation,
} = savingsAPI;
